// src/public/PublicInvoicePage.js
// Customer-facing invoice page — no auth, no sidebar, no tenant header
// Token read from URL: /public/invoice/:token
// Business + branding returned directly from GET /public/invoice/:token
// Calls POST /public/invoice/:token/pay → redirects to Stripe checkout
// Handles ?success=1 and ?canceled=1 query params on return
// Item fields: item.qty, item.rate, item.description — matches DB items_json
// Zero JOBFLOW NOW branding — subscriber identity only

import { useState, useEffect, useRef } from 'react';
import { getApiBase } from '../apiBase';
import InvoicePrint from '../invoiceprint';

const API_BASE = getApiBase();

const PublicInvoicePage = () => {
  const [invoice, setInvoice]   = useState(null);
  const [business, setBusiness] = useState(null);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [paying, setPaying]     = useState(false);
  const [error, setError]       = useState('');
  const viewTrackingStartedRef  = useRef(false);

  const token = window.location.pathname.split('/').pop();

  const params      = new URLSearchParams(window.location.search);
  const paySuccess  = params.get('success')  === '1';
  const payCanceled = params.get('canceled') === '1';

  useEffect(() => {
    if (!token) { setError('Invalid link — no token found.'); setLoading(false); return; }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!invoice || !token || viewTrackingStartedRef.current) return undefined;

    const trackIfVisible = () => {
      if (document.visibilityState !== 'visible' || viewTrackingStartedRef.current) return;

      viewTrackingStartedRef.current = true;
      document.removeEventListener('visibilitychange', trackIfVisible);

      fetch(`${API_BASE}/public/invoice/${token}/view`, {
        method: 'POST',
        keepalive: true
      }).catch(() => undefined);
    };

    trackIfVisible();

    if (!viewTrackingStartedRef.current) {
      document.addEventListener('visibilitychange', trackIfVisible);
    }

    return () => {
      document.removeEventListener('visibilitychange', trackIfVisible);
    };
  }, [invoice, token]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const invRes = await fetch(`${API_BASE}/public/invoice/${token}`);
      if (invRes.status === 404) { setError('This invoice link was not found.'); return; }
      if (invRes.status === 410) { setError('This link has expired or been revoked.'); return; }
      if (!invRes.ok) { setError('Failed to load invoice.'); return; }
      const invData = await invRes.json();
      setInvoice(invData.invoice);
      setBusiness(invData.business || null);
      setBranding(invData.branding || null);
    } catch (e) {
      setError('Network error — please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePay = async () => {
    setPaying(true);
    try {
      const res = await fetch(`${API_BASE}/public/invoice/${token}/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (res.status === 409) { setError(data.error || 'This invoice is already paid.'); return; }
      if (!res.ok) { setError(data.error || 'Payment initiation failed. Please try again.'); return; }
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        setError('No checkout URL returned. Please contact support.');
      }
    } catch (e) {
      setError('Network error — please try again.');
    } finally {
      setPaying(false);
    }
  };

  const fmt = (val) => {
    const n = Number(val);
    return (Number.isFinite(n) ? n : 0).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  const pageStyle = { minHeight: '100vh', backgroundColor: '#e9eef5', padding: 'clamp(10px, 4vw, 40px)', fontFamily: 'Arial, sans-serif', boxSizing: 'border-box' };
  const cardStyle = { maxWidth: '800px', margin: '0 auto' };

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={{ ...cardStyle, textAlign: 'center', padding: '60px', backgroundColor: '#fff', border: '4px solid black' }}>
          <div style={{ fontWeight: 900, fontSize: '18px' }}>⏳ LOADING INVOICE...</div>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ padding: '30px', textAlign: 'center', fontWeight: 900, fontSize: '16px', border: '4px solid black', backgroundColor: '#fee2e2' }}>
            ⚠️ {error || 'Invoice not found.'}
          </div>
        </div>
      </div>
    );
  }

  const status     = String(invoice.status || 'UNPAID').toUpperCase();
  const isPaid     = status === 'PAID';
  const balanceDue = Number(invoice.balance_due || 0);

  // Inject branding and business into the invoice object so InvoicePrint can
  // use them without hitting the authenticated internal API endpoints.
  // __isBrandingPreview: true → InvoicePrint reads inv.branding instead of fetching.
  // __business* fields     → InvoicePrint reads these in the preview business path.
  const mergedInvoice = {
    ...invoice,
    __isBrandingPreview: true,
    branding,
    __businessName:    business?.businessName || '',
    __businessAddress: business?.address      || '',
    __businessCity:    business?.city         || '',
    __businessState:   business?.state        || '',
    __businessZip:     business?.zip          || '',
    __businessPhone:   business?.phone || business?.mobile || '',
    __businessEmail:   business?.email        || '',
    __businessWebsite: business?.website      || '',
    __publicBusinessSettings: business || {},
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>

        {/* Stripe success banner */}
        {paySuccess && (
          <div style={{ padding: '30px', border: '4px solid black', backgroundColor: '#dcfce7', fontWeight: 900, fontSize: '18px', textAlign: 'center', marginBottom: '20px' }}>
            ✅ PAYMENT RECEIVED — Thank you! Your invoice has been marked as paid.
          </div>
        )}

        {/* Stripe canceled banner */}
        {payCanceled && !paySuccess && (
          <div style={{ padding: '20px', border: '4px solid black', backgroundColor: '#fef9c3', fontWeight: 900, fontSize: '15px', textAlign: 'center', marginBottom: '20px' }}>
            ⚠️ Payment was canceled. You can try again below.
          </div>
        )}

        <InvoicePrint invoice={mergedInvoice} docType="INVOICE" />

        {/* Pay Button */}
        {!isPaid && !paySuccess && balanceDue > 0 && (
          <button
            style={{
              width: '100%', padding: '20px', backgroundColor: '#39FF14', color: 'black',
              border: '4px solid black', fontWeight: 900, fontSize: 'clamp(13px, 4.5vw, 20px)',
              cursor: paying ? 'not-allowed' : 'pointer', textTransform: 'uppercase',
              letterSpacing: '1px', marginTop: '24px', opacity: paying ? 0.6 : 1,
              boxSizing: 'border-box', overflowWrap: 'normal', wordBreak: 'normal'
            }}
            onClick={handlePay}
            disabled={paying}
          >
            {paying ? '⏳ REDIRECTING TO PAYMENT...' : `💳 PAY NOW — ${fmt(balanceDue)}`}
          </button>
        )}

        {/* Paid confirmation */}
        {(isPaid || paySuccess) && (
          <div style={{ padding: '20px', border: '4px solid black', backgroundColor: '#dcfce7', fontWeight: 900, fontSize: '16px', textAlign: 'center', marginTop: '20px' }}>
            ✅ THIS INVOICE HAS BEEN PAID — Thank you!
          </div>
        )}

      </div>
    </div>
  );
};

export default PublicInvoicePage;
